import { useState, useEffect } from "react"
import { HexColorPicker } from "react-colorful"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  ArrowLeft,
  Plus,
  Wallet,
  MoreVertical,
  Edit,
  Trash2,
  Star,
  TrendingUp,
  CreditCard,
  Receipt,
} from "lucide-react"
import { useAuth } from "@/lib/auth/auth-context"
import { useAccountSelection } from "@/lib/context/account-selection-context"
import {
  fetchSubAccountsWithStats,
  createSubAccount,
  updateSubAccount,
  deleteSubAccount,
  setDefaultSubAccount,
  toggleSubAccountActive,
} from "@/lib/services/sub-accounts"
import type { SubAccountWithStats, SubAccount } from "@/lib/types"
import { toast } from "sonner"
import { convertTailwindToHex, getColorBackground } from "@/lib/utils/color-helpers"

interface SubAccountsProps {
  onBack: () => void
}

export function SubAccounts({ onBack }: SubAccountsProps) {
  const { user } = useAuth()
  const { refreshSubAccounts } = useAccountSelection()
  const [accounts, setAccounts] = useState<SubAccountWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [editingAccount, setEditingAccount] = useState<SubAccountWithStats | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    type: "personal" as "personal" | "business" | "savings" | "other",
    color: "#8B5CF6",
    spendingLimit: "",
    isActive: true,
  })

  useEffect(() => {
    loadAccounts()
  }, [user])

  const loadAccounts = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      const data = await fetchSubAccountsWithStats(user.id)
      setAccounts(data)
    } catch (error) {
      console.error("Error loading sub-accounts:", error)
      toast.error("Failed to load sub-accounts")
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (account?: SubAccountWithStats) => {
    if (account) {
      setEditingAccount(account)
      setFormData({
        name: account.name,
        type: account.type,
        color: convertTailwindToHex(account.color),
        spendingLimit: account.spendingLimit?.toString() || "",
        isActive: account.isActive,
      })
    } else {
      setEditingAccount(null)
      setFormData({
        name: "",
        type: "personal",
        color: "#8B5CF6",
        spendingLimit: "",
        isActive: true,
      })
    }
    setShowDialog(true)
  }

  const handleCloseDialog = () => {
    setShowDialog(false)
    setEditingAccount(null)
  }

  const handleSubmit = async () => {
    if (!user?.id) return

    if (!formData.name.trim()) {
      toast.error("Please enter a name for the sub-account")
      return
    }

    try {
      if (editingAccount) {
        await updateSubAccount(editingAccount.id, {
          name: formData.name,
          type: formData.type,
          color: formData.color,
          spendingLimit: formData.spendingLimit ? parseFloat(formData.spendingLimit) : null,
          isActive: formData.isActive,
        })
        toast.success("Sub-account updated successfully")
      } else {
        await createSubAccount(user.id, {
          name: formData.name,
          type: formData.type,
          icon: '💳',
          color: formData.color,
          isPrimary: false,
          isActive: formData.isActive,
          spendingLimit: formData.spendingLimit ? parseFloat(formData.spendingLimit) : null,
        })
        toast.success("Sub-account created successfully")
      }

      handleCloseDialog()
      loadAccounts()
      // Refresh global context so dropdown updates immediately
      await refreshSubAccounts()
    } catch (error) {
      console.error("Error saving sub-account:", error)
      toast.error("Failed to save sub-account")
    }
  }

  const handleDelete = async (accountId: string) => {
    if (!confirm("Are you sure you want to delete this sub-account? This action cannot be undone.")) {
      return
    }

    try {
      await deleteSubAccount(accountId)
      toast.success("Sub-account deleted successfully")
      loadAccounts()
      // Refresh global context so dropdown updates immediately
      await refreshSubAccounts()
    } catch (error: any) {
      console.error("Error deleting sub-account:", error)
      toast.error(error.message || "Failed to delete sub-account")
    }
  }

  const handleSetPrimary = async (accountId: string) => {
    if (!user?.id) return

    try {
      await setDefaultSubAccount(user.id, accountId)
      toast.success("Primary account updated")
      loadAccounts()
      // Refresh global context so dropdown updates immediately
      await refreshSubAccounts()
    } catch (error) {
      console.error("Error setting primary account:", error)
      toast.error("Failed to set primary account")
    }
  }

  const handleToggleActive = async (accountId: string, isActive: boolean) => {
    try {
      await toggleSubAccountActive(accountId, !isActive)
      toast.success(`Sub-account ${!isActive ? "activated" : "deactivated"}`)
      loadAccounts()
      // Refresh global context so dropdown updates immediately
      await refreshSubAccounts()
    } catch (error) {
      console.error("Error toggling account status:", error)
      toast.error("Failed to update account status")
    }
  }

  const typeIcons = {
    personal: "👤",
    business: "💼",
    savings: "💰",
    other: "📦",
  }

  return (
    <div className="space-y-6 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-6 w-6" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">Sub-Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Organize your finances into separate accounts
          </p>
        </div>
      </div>

      {/* Add Sub-Account Button */}
      <Card className="glassmorphism p-4 border-white/20">
        <Button
          onClick={() => handleOpenDialog()}
          className="w-full gap-2 bg-primary/90 hover:bg-primary backdrop-blur-xl"
        >
          <Plus className="h-5 w-5" />
          Add New Sub-Account
        </Button>
      </Card>

      {/* Sub-Accounts List */}
      <div className="space-y-4">
        {loading ? (
          <Card className="glassmorphism p-8 text-center border-white/20">
            <p className="text-sm text-muted-foreground">Loading sub-accounts...</p>
          </Card>
        ) : accounts.length === 0 ? (
          <Card className="glassmorphism p-8 text-center border-white/20">
            <div className="flex flex-col items-center gap-3">
              <div className="h-16 w-16 rounded-full bg-primary/10 backdrop-blur-xl flex items-center justify-center">
                <Wallet className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="font-medium">No sub-accounts yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Create sub-accounts to organize your finances
                </p>
              </div>
            </div>
          </Card>
        ) : (
          accounts.map((account) => (
            <Card
              key={account.id}
              className="glassmorphism p-4 border-white/20 hover:bg-white/15 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div
                  className="h-14 w-14 rounded-xl backdrop-blur-xl flex items-center justify-center text-2xl"
                  style={getColorBackground(account.color)}
                >
                  {account.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{account.name}</p>
                    {account.isPrimary && (
                      <Badge variant="secondary" className="gap-1">
                        <Star className="h-3 w-3 fill-current" />
                        Primary
                      </Badge>
                    )}
                    {!account.isActive && (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground capitalize">
                    {typeIcons[account.type]} {account.type}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg bg-success/10 flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-success" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Balance</p>
                        <p className="text-sm font-semibold">
                          ${account.balance.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <CreditCard className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Cards</p>
                        <p className="text-sm font-semibold">{account.cardCount}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg bg-chart-2/10 flex items-center justify-center">
                        <Receipt className="h-4 w-4 text-chart-2" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Transactions</p>
                        <p className="text-sm font-semibold">{account.transactionCount}</p>
                      </div>
                    </div>
                    {account.spendingLimit && (
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-chart-4/10 flex items-center justify-center">
                          <span className="text-xs font-bold">
                            {Math.round(account.budgetUsage)}%
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Budget</p>
                          <p className="text-sm font-semibold">
                            ${account.spendingLimit.toFixed(0)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="backdrop-blur-xl">
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glassmorphism border-white/20">
                    {!account.isPrimary && (
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => handleSetPrimary(account.id)}
                      >
                        <Star className="h-4 w-4" />
                        Set as Primary
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={() => handleOpenDialog(account)}
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={() => handleToggleActive(account.id, account.isActive)}
                    >
                      {account.isActive ? "Deactivate" : "Activate"}
                    </DropdownMenuItem>
                    {!account.isPrimary && (
                      <DropdownMenuItem
                        className="gap-2 text-destructive"
                        onClick={() => handleDelete(account.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="glassmorphism border-white/20">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? "Edit Sub-Account" : "Create Sub-Account"}
            </DialogTitle>
            <DialogDescription>
              {editingAccount
                ? "Update your sub-account details"
                : "Create a new sub-account to organize your finances"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Personal, Business, Savings"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Type</label>
              <Select
                value={formData.type}
                onValueChange={(value: any) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">👤 Personal</SelectItem>
                  <SelectItem value="business">💼 Business</SelectItem>
                  <SelectItem value="savings">💰 Savings</SelectItem>
                  <SelectItem value="other">📦 Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Color</label>
              <div className="mt-2 space-y-3">
                <div className="flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-lg border-2 border-border shadow-sm"
                    style={{ backgroundColor: formData.color }}
                  />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">Selected color</p>
                    <p className="text-sm font-mono font-semibold">{formData.color.toUpperCase()}</p>
                  </div>
                </div>
                <HexColorPicker
                  color={formData.color}
                  onChange={(color) => setFormData({ ...formData, color })}
                  className="!w-full"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">
                Spending Limit (optional)
              </label>
              <Input
                type="number"
                value={formData.spendingLimit}
                onChange={(e) =>
                  setFormData({ ...formData, spendingLimit: e.target.value })
                }
                placeholder="0.00"
                className="mt-1"
                step="0.01"
                min="0"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Monthly spending limit for this account
              </p>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Active</label>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isActive: checked })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingAccount ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
